import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useCoachChat,
  type CoachHistoryScope,
  type CoachInsight,
  type CoachChatContextType,
} from "@/hooks/useCoachChat";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import { appQueryKeys } from "@/lib/queryKeys";
import { captureTrialEvent } from "@/lib/trialAnalytics";
import { useCoachPortfolioRuntime } from "@/hooks/useCoachPortfolioRuntime";
import { buildCoachIdentityTemplate } from "@/lib/behavior/coachIdentityTemplate";
import {
  parseCoachIdentityBuilderDraft,
  saveCoachIdentityBuilderDraft,
} from "@/lib/coachIdentityHandoff";
import {
  buildCoachIdentityGuidedFormRoute,
  COACH_IDENTITY_BUILDER_KICKOFF_PROMPT,
  COACH_IDENTITY_PHILOSOPHY_KICKOFF_PROMPT,
} from "@/lib/coachIdentityRoute";
import { buildVeeraThreadsFromMessages } from "@/lib/veeraThreads";
import { deriveCoachLensStatus } from "@/lib/behavior/coachLensStatus";
import { useVeeraThreads } from "@/hooks/useVeeraThreads";
import {
  buildGeneratedVeeraThreadTitle,
  filterHomogeneousVeeraSessions,
  readPersistedVeeraDeletedThreadIds,
} from "@/lib/veeraThreads";
import {
  parseCoachVeeraContextualEntry,
  type CoachVeeraContextualEntry,
} from "@/lib/coachVeeraContextualEntry";
import { type VeeraContextualThreadInput } from "@/lib/veeraContextualThreads";
import {
  mergePersistedVeeraThreads,
  resolveHydratedVeeraActiveThreadId,
  resolveActiveVeeraThreadPresentation,
} from "@/lib/veeraThreadHydration";

type Leader = {
  id: string;
  name: string;
  email: string;
  behaviorName: string | null;
  phase: string | null;
  streak: number;
};

type CoachThread = {
  id: string;
  title: string;
  preview: string;
  updatedAt: string;
  contextType: CoachChatContextType;
  leaderId: string | null;
  sourceLabel?: string | null;
  fixedTitle?: string | null;
  contextualThreadInput?: VeeraContextualThreadInput | null;
  creationOrigin?: "user";
};

type CoachSidebarThread = {
  id: string;
  title: string;
  preview: string;
  updatedAt: string;
  tagLabel?: string;
};

type CoachChatMessageRow = {
  id: string;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  context_type: CoachChatContextType;
  leader_id: string | null;
};

type CoachIdentityBuilderSessionRow = {
  chat_session_id: string;
  status: "active" | "completed" | "discarded";
  updated_at: string;
};

type LeaderRelationshipScope = {
  scopeKey: string;
  status: "loading" | "ready" | "unavailable";
  relationshipRevision: string | null;
  relationshipCreatedAt: string | null;
};

type LeaderRelationshipPortfolioRow = {
  leader_id: string;
  relationship_revision: string;
  created_at: string;
};

type LeaderRelationshipChangePayload = {
  new?: unknown;
  old?: unknown;
};

const RELATIONSHIP_ANCHOR_AUDIT_TIMEOUT_MS = 10_000;

const resolveWithinRelationshipAuditWindow = <T,>(
  query: PromiseLike<T>,
  abortController: AbortController,
): Promise<T> =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(
      () => {
        abortController.abort();
        reject(new Error("Relationship anchor audit timed out"));
      },
      RELATIONSHIP_ANCHOR_AUDIT_TIMEOUT_MS,
    );
    Promise.resolve(query).then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });

const resolveChangedLeaderId = (payload: unknown): string | null => {
  if (!payload || typeof payload !== "object") return null;
  const change = payload as LeaderRelationshipChangePayload;
  for (const candidate of [change.new, change.old]) {
    if (!candidate || typeof candidate !== "object") continue;
    const leaderId = (candidate as { leader_id?: unknown }).leader_id;
    if (typeof leaderId === "string" && leaderId.trim().length > 0) {
      return leaderId;
    }
  }
  return null;
};

const isValidLeaderRelationshipPortfolioRow = (
  value: unknown,
): value is LeaderRelationshipPortfolioRow => {
  if (!value || typeof value !== "object") return false;
  const row = value as Partial<LeaderRelationshipPortfolioRow>;
  return (
    typeof row.leader_id === "string" &&
    row.leader_id.trim().length > 0 &&
    typeof row.relationship_revision === "string" &&
    row.relationship_revision.trim().length > 0 &&
    typeof row.created_at === "string" &&
    Number.isFinite(Date.parse(row.created_at))
  );
};

const GLOBAL_PROMPTS = [
  {
    id: "triage",
    label: "Portfolio triage",
    prompt:
      "Review three leaders who may need attention this week, and explain why in one line each.",
  },
  {
    id: "patterns",
    label: "Pattern scan",
    prompt:
      "Summarize cross-leader patterns from recent moments and flag where evidence is thin.",
  },
  {
    id: "sequencing",
    label: "Sequencing",
    prompt:
      "What sequencing decisions should I be considering this month across my active leaders?",
  },
  {
    id: "coach-reflect",
    label: "Coach reflection",
    prompt:
      "Give me one reflection prompt to improve my coaching approach this week.",
  },
];

const LEADER_PROMPTS = [
  {
    id: "diagnose",
    label: "Explore",
    prompt:
      "Summarize what pattern is strongest for this leader and where context is still thin.",
  },
  {
    id: "prep",
    label: "Session prep",
    prompt:
      "Prepare my next coaching conversation with one high-leverage move and two follow-up questions.",
  },
  {
    id: "draft",
    label: "Draft message",
    prompt:
      "Draft a concise message I can send to this leader based on recent evidence.",
  },
  {
    id: "readiness",
    label: "Evidence review",
    prompt:
      "What does the current evidence suggest about sequencing or graduation review for this leader?",
  },
];

const COACH_PROMPTS = [
  {
    id: "coach-strengths",
    label: "What worked",
    prompt:
      "From my recent sessions, what coaching moves seem to be working well and why?",
  },
  {
    id: "coach-growth",
    label: "Growth edge",
    prompt:
      "What is one growth edge in my coaching craft from recent evidence?",
  },
  {
    id: "coach-questions",
    label: "Reflective questions",
    prompt:
      "Ask me three reflective coaching questions based on what you observed in my recent sessions.",
  },
  {
    id: "coach-experiment",
    label: "Next experiment",
    prompt:
      "Suggest one next-session coaching experiment with a clear intention and watchout.",
  },
];

type CoachLensTimestampSource = {
  coach_lens_updated_at?: string | null;
  updated_at?: string | null;
  updatedAt?: string | null;
};

const COACH_IDENTITY_PHILOSOPHY_PROMPTS = [
  {
    id: "philosophy-start",
    label: "Ask me one question",
    prompt:
      "Ask me one focused question to uncover what I believe about how leaders grow.",
  },
  {
    id: "philosophy-examples",
    label: "Show examples",
    prompt:
      "Show me a few coaching philosophy examples and help me react to what fits.",
  },
  {
    id: "philosophy-frameworks",
    label: "Compare frameworks",
    prompt:
      "Compare a few coaching frameworks against my style and suggest where to start.",
  },
];

export const COACH_AI_HISTORY_SCOPES: Array<{
  value: CoachHistoryScope;
  label: string;
}> = [
  { value: "recent", label: "14d" },
  { value: "rolling_30d", label: "30d" },
  { value: "rolling_60d", label: "60d" },
  { value: "rolling_90d", label: "90d" },
  { value: "rolling_365d", label: "1y" },
  { value: "full", label: "All" },
];

const WORK_TYPE_LABELS: Record<CoachChatContextType, string> = {
  global: "Portfolio",
  leader: "Leaders",
  coach: "My Coaching",
};

const NEW_THREAD_LABELS: Record<CoachChatContextType, string> = {
  global: "New portfolio conversation",
  leader: "New leader analysis",
  coach: "New coaching thread",
};

const CONTEXT_SUBTITLES: Record<CoachChatContextType, string> = {
  global: "Cross-leader patterns and sequencing",
  leader: "Leader analysis and preparation",
  coach: "Coaching supervision and craft reflection",
};

const COACH_IDENTITY_BUILDER_PROMPT = COACH_IDENTITY_BUILDER_KICKOFF_PROMPT;
const COACH_IDENTITY_PHILOSOPHY_PROMPT =
  COACH_IDENTITY_PHILOSOPHY_KICKOFF_PROMPT;
const COACH_IDENTITY_BUILDER_THREAD_TITLE = "My Coaching System";
const COACH_IDENTITY_PHILOSOPHY_THREAD_TITLE = "Coaching Philosophy";
const COACH_IDENTITY_PHILOSOPHY_REVIEW_DRAFT_PROMPT =
  "Draft what we have so far into a coaching philosophy I can review in My Coaching System. Keep it concise and name any gaps instead of inventing details.";
const COACH_IDENTITY_SYSTEM_REVIEW_DRAFT_PROMPT =
  "Draft what we have so far into a My Coaching System update I can review. Keep it concise and name any gaps instead of inventing details.";

const normalizeCoachWorkType = (
  contextType: CoachChatContextType,
  coachModeEnabled: boolean,
): CoachChatContextType =>
  contextType === "coach" && !coachModeEnabled ? "global" : contextType;

const resolveCoachRouteThreadContextType = (params: {
  isCoachIdentityBuilder: boolean;
  resolvedUrlContextType: CoachChatContextType;
  coachModeEnabled: boolean;
}): CoachChatContextType =>
  params.isCoachIdentityBuilder
    ? "coach"
    : normalizeCoachWorkType(
        params.resolvedUrlContextType,
        params.coachModeEnabled,
      );

const resolveCoachRouteThreadFixedTitle = (
  action: string | null,
  source: string | null,
  contextType: CoachChatContextType,
) => {
  if (action === "coach-identity-builder") {
    return source === "coaching-philosophy"
      ? COACH_IDENTITY_PHILOSOPHY_THREAD_TITLE
      : COACH_IDENTITY_BUILDER_THREAD_TITLE;
  }
  if (contextType !== "leader") return null;
  if (action === "draft-message") return "Draft Message";
  if (action === "sequencing") return "Help Think Through Sequencing";
  return null;
};

const createThread = (
  contextType: CoachChatContextType,
  leaderId: string | null,
  fixedTitle?: string | null,
): CoachThread => ({
  id: crypto.randomUUID(),
  title:
    fixedTitle ||
    (contextType === "global"
      ? "Portfolio conversation"
      : contextType === "leader"
        ? "Leader analysis"
        : "Coaching reflection"),
  preview: "",
  updatedAt: new Date().toISOString(),
  contextType,
  leaderId,
  fixedTitle: fixedTitle ?? null,
});

const COACH_THREAD_PLACEHOLDER_TITLES: Record<CoachChatContextType, string> = {
  global: "Portfolio conversation",
  leader: "Leader analysis",
  coach: "Coaching reflection",
};

export function useCoachAiPageController() {
  const { user } = useAuth();
  const trialAccess = useTrialAccess({ role: "coach" });
  const premiumActivityLocked = Boolean(
    !trialAccess.isLoading &&
      trialAccess.presentation.state !== "not_started" &&
      !trialAccess.canCreatePremiumActivity,
  );
  const premiumActivityChecking = trialAccess.isLoading;
  const premiumActivityExpired =
    trialAccess.presentation.state === "expired";
  const premiumActivityUnavailable =
    premiumActivityChecking || premiumActivityLocked;
  const lockedBotViewedRef = useRef(false);
  const {
    coachModeEnabled,
    isLoading: areFeatureFlagsLoading,
    isInitialResolutionPending: isFeatureFlagInitialResolutionPending = false,
  } = useFeatureFlags();
  const areFeatureFlagsResolving = Boolean(
    areFeatureFlagsLoading
      || (!coachModeEnabled && isFeatureFlagInitialResolutionPending),
  );
  const coachModeAvailableForPresentation = coachModeEnabled || areFeatureFlagsResolving;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const urlLeaderId = searchParams.get("leaderId");
  const urlAction = searchParams.get("action");
  const urlSource = searchParams.get("source");
  const urlContextType = searchParams.get("contextType");
  const urlPrompt = searchParams.get("prompt");
  const urlTranscriptId = searchParams.get("transcriptId");
  const urlProvenanceId = searchParams.get("provenanceId");
  const urlFrom = searchParams.get("from");
  const urlSignalType = searchParams.get("signalType");
  const urlSignalLevel = searchParams.get("signalLevel");
  const urlSignals = searchParams.get("signals");
  const contextualEntryParam = searchParams.get("contextualEntry");
  const contextualEntry = useMemo<CoachVeeraContextualEntry | null>(
    () => parseCoachVeeraContextualEntry(contextualEntryParam),
    [contextualEntryParam],
  );
  const isCoachIdentityBuilder = urlAction === "coach-identity-builder";
  const isCoachIdentityPhilosophyBuilder =
    isCoachIdentityBuilder && urlSource === "coaching-philosophy";
  const resolvedUrlContextType: CoachChatContextType =
    urlContextType === "coach" ? "coach" : urlLeaderId ? "leader" : "global";
  const routeThreadFixedTitle = resolveCoachRouteThreadFixedTitle(
    urlAction,
    urlSource,
    resolvedUrlContextType,
  );

  const [inputValue, setInputValue] = useState("");
  const [historyScope, setHistoryScope] =
    useState<CoachHistoryScope>("rolling_90d");
  const [hasTriggeredAutoPrompt, setHasTriggeredAutoPrompt] = useState(false);
  const [hasResolvedBuilderResume, setHasResolvedBuilderResume] = useState(
    !isCoachIdentityBuilder,
  );
  const [
    hasResolvedPersistedThreadHydration,
    setHasResolvedPersistedThreadHydration,
  ] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [leaderPickerOpen, setLeaderPickerOpen] = useState(false);
  const [leaderSearch, setLeaderSearch] = useState("");
  const [coachModeFallbackErrorThreadId, setCoachModeFallbackErrorThreadId] =
    useState<string | null>(null);
  const [coachModeFallbackRetryNonce, setCoachModeFallbackRetryNonce] =
    useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const handledAutoPromptKeyRef = useRef<string | null>(null);
  const lastThreadMessageSignatureRef = useRef<{
    threadId: string | null;
    signature: string;
  }>({
    threadId: null,
    signature: "",
  });
  const initialWorkType = normalizeCoachWorkType(
    resolvedUrlContextType,
    coachModeAvailableForPresentation,
  );
  const [selectedWorkType, setSelectedWorkType] =
    useState<CoachChatContextType>(initialWorkType);
  const createInitialCoachThreads = useCallback(() => {
    if (contextualEntry) {
      return [
        {
          ...createThread(
            contextualEntry.contextType,
            contextualEntry.contextType === "global"
              ? null
              : contextualEntry.leaderId,
          ),
          title: contextualEntry.threadTitle,
          preview: contextualEntry.assistantOpener.carryOverSummary,
          sourceLabel: contextualEntry.sourceLabel,
          fixedTitle: contextualEntry.threadTitle,
          contextualThreadInput: contextualEntry.contextualThreadInput,
        },
      ];
    }
    const initialContextType = resolveCoachRouteThreadContextType({
      isCoachIdentityBuilder,
      resolvedUrlContextType,
      coachModeEnabled: coachModeAvailableForPresentation,
    });
    return [
      createThread(
        initialContextType,
        initialContextType === "leader" ? urlLeaderId : null,
        routeThreadFixedTitle,
      ),
    ];
  }, [
    contextualEntry,
    isCoachIdentityBuilder,
    resolvedUrlContextType,
    coachModeAvailableForPresentation,
    routeThreadFixedTitle,
    urlLeaderId,
  ]);
  const { threads, setThreads, activeThreadId, setActiveThreadId, hideThread } =
    useVeeraThreads<CoachThread>({
      surface: "coach",
      userId: user?.id,
      contextualThreadInput: contextualEntry?.contextualThreadInput ?? null,
      createInitialThreads: createInitialCoachThreads,
    });
  const pendingHydratedThreadsRef = useRef<CoachThread[] | null>(null);
  const currentThreadsRef = useRef<CoachThread[]>(threads);
  const relationshipAnchorAuditGenerationRef = useRef(0);
  const relationshipAnchorAuditRunTokenRef = useRef<symbol | null>(null);
  const relationshipAnchorAuditQueuedRef = useRef(false);
  const relationshipAnchorAuditAbortRef = useRef<AbortController | null>(null);
  const relationshipAnchorAuditRetryTimeoutRef = useRef<number | null>(null);
  const relationshipAnchorAuditCoachIdRef = useRef<string | null>(
    user?.id ?? null,
  );
  const relationshipAnchorAuditMountedRef = useRef(true);
  const [isRelationshipAnchorAuditPending, setIsRelationshipAnchorAuditPending] =
    useState(false);
  const [relationshipAnchorAuditVerifiedCoachId, setRelationshipAnchorAuditVerifiedCoachId] =
    useState<string | null>(null);
  const shouldQuarantineLeaderRelationshipThreads =
    isRelationshipAnchorAuditPending ||
    relationshipAnchorAuditVerifiedCoachId !== (user?.id ?? null) ||
    relationshipAnchorAuditCoachIdRef.current !== (user?.id ?? null);
  const [portfolioRelationshipRefreshNonce, setPortfolioRelationshipRefreshNonce] =
    useState(0);

  useLayoutEffect(() => {
    currentThreadsRef.current = threads;
  }, [threads]);

  useEffect(() => {
    relationshipAnchorAuditCoachIdRef.current = user?.id ?? null;
    relationshipAnchorAuditMountedRef.current = true;
    setIsRelationshipAnchorAuditPending(false);
    setRelationshipAnchorAuditVerifiedCoachId(null);
    return () => {
      relationshipAnchorAuditMountedRef.current = false;
      relationshipAnchorAuditCoachIdRef.current = null;
      relationshipAnchorAuditGenerationRef.current += 1;
      relationshipAnchorAuditQueuedRef.current = false;
      relationshipAnchorAuditRunTokenRef.current = null;
      relationshipAnchorAuditAbortRef.current?.abort();
      relationshipAnchorAuditAbortRef.current = null;
      if (relationshipAnchorAuditRetryTimeoutRef.current !== null) {
        window.clearTimeout(relationshipAnchorAuditRetryTimeoutRef.current);
        relationshipAnchorAuditRetryTimeoutRef.current = null;
      }
    };
  }, [user?.id]);

  const { data: leaders } = useQuery({
    queryKey: appQueryKeys.coachAiLeaders(user?.id),
    queryFn: async (): Promise<Leader[]> => {
      if (!user) return [];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("coach_id", user.id);

      if (!profiles) return [];

      return Promise.all(
        profiles.map(async (profile) => {
          const { data: behaviorRow } = await supabase
            .from("leader_behaviors")
            .select(`phase, streak_current, behaviors (name)`)
            .eq("leader_id", profile.id)
            .in("phase", ["ACTIVE_PRACTICE", "CONSOLIDATION", "SYNTHESIS"])
            .limit(1)
            .maybeSingle();

          const behaviorData = behaviorRow?.behaviors as {
            name?: string;
          } | null;

          return {
            id: profile.id,
            name: profile.full_name || "Unknown",
            email: profile.email || "",
            behaviorName: behaviorData?.name || null,
            phase: behaviorRow?.phase || null,
            streak: behaviorRow?.streak_current || 0,
          };
        }),
      );
    },
    enabled: Boolean(user),
  });

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setHasResolvedPersistedThreadHydration(false);

    const loadPersistedThreads = async () => {
      const { data, error } = await (
        (supabase as unknown as { from: (table: string) => unknown }).from(
          "coach_chat_messages",
        ) as {
          select: (columns: string) => {
            eq: (
              column: string,
              value: string,
            ) => {
              order: (
                column: string,
                options: { ascending: boolean },
              ) => Promise<{
                data: CoachChatMessageRow[] | null;
                error: { message?: string } | null;
              }>;
            };
          };
        }
      )
        .select(
          "id, session_id, role, content, created_at, context_type, leader_id",
        )
        .eq("coach_id", user.id)
        .order("created_at", { ascending: false });

      if (cancelled) return;
      if (error || data === null) {
        // A failed all-session audit cannot prove that cached thread metadata
        // is homogeneous. Keep server history intact, but replace the local
        // unverified session pointers with one fresh route-compatible thread.
        const recoveryThreads = createInitialCoachThreads();
        pendingHydratedThreadsRef.current = recoveryThreads;
        setThreads(recoveryThreads);
        setActiveThreadId(recoveryThreads[0]?.id ?? null);
        setHasResolvedPersistedThreadHydration(true);
        return;
      }
      if (data.length === 0) {
        setHasResolvedPersistedThreadHydration(true);
        return;
      }

      const homogeneousRows = filterHomogeneousVeeraSessions(data);
      const leaderIds = Array.from(
        new Set(
          homogeneousRows
            .filter((row) => row.context_type === "leader")
            .map((row) => row.leader_id)
            .filter((leaderId): leaderId is string => Boolean(leaderId)),
        ),
      );
      let relationshipRows: unknown[] | null = [];
      let relationshipLookupFailed = false;
      if (leaderIds.length > 0) {
        try {
          const { data: currentRelationships, error: relationshipError } =
            await supabase
              .from("coach_leader_links")
              .select("leader_id, relationship_revision, created_at")
              .eq("coach_id", user.id)
              .in("leader_id", leaderIds);
          relationshipLookupFailed = Boolean(relationshipError) || currentRelationships === null;
          relationshipRows = relationshipLookupFailed ? null : currentRelationships;
        } catch {
          relationshipLookupFailed = true;
          relationshipRows = null;
        }
      }
      if (cancelled) return;

      const relationshipsByLeaderId = new Map<
        string,
        LeaderRelationshipPortfolioRow | null
      >();
      if (!relationshipLookupFailed && relationshipRows) {
        for (const candidate of relationshipRows) {
          const candidateLeaderId =
            candidate && typeof candidate === "object"
              ? (candidate as { leader_id?: unknown }).leader_id
              : null;
          if (
            typeof candidateLeaderId !== "string" ||
            !leaderIds.includes(candidateLeaderId)
          ) {
            continue;
          }
          if (!isValidLeaderRelationshipPortfolioRow(candidate)) {
            relationshipsByLeaderId.set(candidateLeaderId, null);
            continue;
          }
          const row = candidate;
          relationshipsByLeaderId.set(
            row.leader_id,
            relationshipsByLeaderId.has(row.leader_id) ? null : row,
          );
        }
      }
      const unsafeRelationshipSessionIds = new Set<string>();
      for (const row of homogeneousRows) {
        if (row.context_type !== "leader") continue;
        const relationship = row.leader_id
          ? relationshipsByLeaderId.get(row.leader_id)
          : null;
        const messageCreatedAt = Date.parse(row.created_at);
        const relationshipCreatedAt = relationship
          ? Date.parse(relationship.created_at)
          : Number.NaN;
        if (
          relationshipLookupFailed ||
          !relationship ||
          !Number.isFinite(messageCreatedAt) ||
          !Number.isFinite(relationshipCreatedAt) ||
          messageCreatedAt < relationshipCreatedAt
        ) {
          unsafeRelationshipSessionIds.add(row.session_id);
        }
      }
      const safeRows = homogeneousRows.filter(
        (row) => !unsafeRelationshipSessionIds.has(row.session_id),
      );
      const safeSessionIds = new Set(safeRows.map((row) => row.session_id));
      const unsafeSessionIds = new Set(
        data
          .map((row) => row.session_id)
          .filter((sessionId) => !safeSessionIds.has(sessionId)),
      );
      const nextThreads = buildVeeraThreadsFromMessages<
        CoachChatMessageRow,
        CoachThread
      >(safeRows, {
        fallbackTitle: (firstMessage) =>
          firstMessage?.context_type === "coach"
            ? "Coaching reflection"
            : firstMessage?.context_type === "leader"
              ? "Leader analysis"
              : "Portfolio conversation",
        titleLimit: 42,
        previewLimit: 86,
        mapThread: (summary, latestMessage) => {
          const isBuilderThread =
            latestMessage.context_type === "coach" &&
            (summary.title
              .toLowerCase()
              .includes("help me build my coach identity") ||
              summary.title
                .toLowerCase()
                .includes("help me refine my coaching system") ||
              summary.title
                .toLowerCase()
                .includes("help me create or refine my coaching philosophy"));
          const isPhilosophyBuilderThread =
            latestMessage.context_type === "coach" &&
            summary.title
              .toLowerCase()
              .includes("help me create or refine my coaching philosophy");
          return {
            ...summary,
            title: isPhilosophyBuilderThread
              ? COACH_IDENTITY_PHILOSOPHY_THREAD_TITLE
              : isBuilderThread
                ? COACH_IDENTITY_BUILDER_THREAD_TITLE
                : summary.title,
            contextType: latestMessage.context_type,
            leaderId: latestMessage.leader_id,
            fixedTitle: isPhilosophyBuilderThread
              ? COACH_IDENTITY_PHILOSOPHY_THREAD_TITLE
              : isBuilderThread
                ? COACH_IDENTITY_BUILDER_THREAD_TITLE
                : null,
          };
        },
      });
      const deletedThreadIds = readPersistedVeeraDeletedThreadIds(
        "coach",
        user.id,
      );
      const visibleThreads = nextThreads.filter(
        (thread) => !deletedThreadIds.includes(thread.id),
      );
      setThreads((current) => {
        // Server rows are authoritative for persisted thread IDs. If a legacy
        // session contains more than one exact scope, quarantine both its rows
        // and any matching local metadata. Local unsent placeholders use IDs
        // absent from the server result and remain available.
        const quarantinedThreads = current.filter(
          (thread) => !unsafeSessionIds.has(thread.id),
        );
        const hydratedThreads = visibleThreads.length > 0
          ? mergePersistedVeeraThreads({
              existingThreads: quarantinedThreads,
              hydratedThreads: visibleThreads,
            })
          : quarantinedThreads;
        const mergedThreads = hydratedThreads.filter((thread) => {
          if (!isCoachIdentityBuilder) return true;
          if (
            thread.id ===
            visibleThreads.find(
              (candidate) =>
                candidate.fixedTitle === COACH_IDENTITY_BUILDER_THREAD_TITLE,
            )?.id
          ) {
            return true;
          }
          return thread.fixedTitle !== COACH_IDENTITY_BUILDER_THREAD_TITLE;
        });
        const reconciledThreads = mergedThreads.length > 0
          ? mergedThreads
          : createInitialCoachThreads();
        pendingHydratedThreadsRef.current = reconciledThreads;
        return reconciledThreads;
      });
      setActiveThreadId((current) => {
        const mergedThreads =
          pendingHydratedThreadsRef.current ?? visibleThreads;
        pendingHydratedThreadsRef.current = null;
        return resolveHydratedVeeraActiveThreadId({
          currentActiveThreadId: current,
          mergedThreads,
        });
      });
      setHasResolvedPersistedThreadHydration(true);
    };

    void loadPersistedThreads();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    urlLeaderId,
    resolvedUrlContextType,
    coachModeEnabled,
    createInitialCoachThreads,
    isCoachIdentityBuilder,
    portfolioRelationshipRefreshNonce,
    setActiveThreadId,
    setThreads,
  ]);

  const requestRelationshipAnchorAudit = useCallback(() => {
    const auditCoachId = user?.id;
    if (
      !auditCoachId ||
      !relationshipAnchorAuditMountedRef.current ||
      relationshipAnchorAuditCoachIdRef.current !== auditCoachId
    ) return;
    if (relationshipAnchorAuditRetryTimeoutRef.current !== null) {
      window.clearTimeout(relationshipAnchorAuditRetryTimeoutRef.current);
      relationshipAnchorAuditRetryTimeoutRef.current = null;
    }
    const requestedGeneration = relationshipAnchorAuditGenerationRef.current + 1;
    relationshipAnchorAuditGenerationRef.current = requestedGeneration;
    setIsRelationshipAnchorAuditPending(true);

    if (relationshipAnchorAuditRunTokenRef.current) {
      relationshipAnchorAuditQueuedRef.current = true;
      return;
    }

    const runAudit = async (
      generation: number,
      coachId: string,
      retryAttempt = 0,
    ): Promise<void> => {
      if (
        !relationshipAnchorAuditMountedRef.current ||
        relationshipAnchorAuditCoachIdRef.current !== coachId
      ) return;
      const runToken = Symbol("relationship-anchor-audit");
      relationshipAnchorAuditRunTokenRef.current = runToken;
      const anchorSnapshot = currentThreadsRef.current.filter(
        (thread) => thread.contextType === "leader" && Boolean(thread.leaderId),
      );
      const leaderIds = Array.from(
        new Set(
          anchorSnapshot
            .map((thread) => thread.leaderId)
            .filter((leaderId): leaderId is string => Boolean(leaderId)),
        ),
      );
      let relationshipRows: unknown[] | null = [];
      let relationshipLookupFailed = false;

      if (leaderIds.length > 0) {
        const abortController = new AbortController();
        relationshipAnchorAuditAbortRef.current = abortController;
        try {
          const { data, error } = await resolveWithinRelationshipAuditWindow(
            supabase
              .from("coach_leader_links")
              .select("leader_id, relationship_revision, created_at")
              .eq("coach_id", coachId)
              .in("leader_id", leaderIds)
              .abortSignal(abortController.signal),
            abortController,
          );
          relationshipLookupFailed = Boolean(error) || data === null;
          relationshipRows = relationshipLookupFailed ? null : data;
        } catch {
          relationshipLookupFailed = true;
          relationshipRows = null;
        } finally {
          if (relationshipAnchorAuditAbortRef.current === abortController) {
            relationshipAnchorAuditAbortRef.current = null;
          }
        }
      }

      if (
        relationshipAnchorAuditMountedRef.current &&
        relationshipAnchorAuditCoachIdRef.current === coachId &&
        generation === relationshipAnchorAuditGenerationRef.current
      ) {
        const relationshipsByLeaderId = new Map<
          string,
          LeaderRelationshipPortfolioRow | null
        >();
        if (!relationshipLookupFailed && relationshipRows) {
          for (const candidate of relationshipRows) {
            const candidateLeaderId =
              candidate && typeof candidate === "object"
                ? (candidate as { leader_id?: unknown }).leader_id
                : null;
            if (
              typeof candidateLeaderId !== "string" ||
              !leaderIds.includes(candidateLeaderId)
            ) {
              continue;
            }
            if (!isValidLeaderRelationshipPortfolioRow(candidate)) {
              relationshipsByLeaderId.set(candidateLeaderId, null);
              continue;
            }
            relationshipsByLeaderId.set(
              candidate.leader_id,
              relationshipsByLeaderId.has(candidate.leader_id) ? null : candidate,
            );
          }
        }

        if (!relationshipLookupFailed) {
          setThreads((current) => {
            const reconciled = current.filter((thread) => {
              if (thread.contextType !== "leader") return true;
              if (!thread.leaderId || !leaderIds.includes(thread.leaderId)) return false;
              const relationship = relationshipsByLeaderId.get(thread.leaderId);
              if (!relationship) return false;
              const relationshipCreatedAt = Date.parse(relationship.created_at);
              const threadUpdatedAt = Date.parse(thread.updatedAt);
              return (
                Number.isFinite(relationshipCreatedAt) &&
                Number.isFinite(threadUpdatedAt) &&
                threadUpdatedAt >= relationshipCreatedAt
              );
            });
            const nextThreads = reconciled.length > 0
              ? reconciled
              : [createThread("global", null)];
            currentThreadsRef.current = nextThreads;
            pendingHydratedThreadsRef.current = nextThreads;
            return nextThreads;
          });
          setActiveThreadId((current) => {
            const reconciledThreads =
              pendingHydratedThreadsRef.current ?? currentThreadsRef.current;
            pendingHydratedThreadsRef.current = null;
            return resolveHydratedVeeraActiveThreadId({
              currentActiveThreadId: current,
              mergedThreads: reconciledThreads,
            });
          });
        }
      }

      if (relationshipAnchorAuditRunTokenRef.current !== runToken) return;
      relationshipAnchorAuditRunTokenRef.current = null;
      if (
        !relationshipAnchorAuditMountedRef.current ||
        relationshipAnchorAuditCoachIdRef.current !== coachId
      ) return;
      if (
        relationshipAnchorAuditQueuedRef.current
      ) {
        relationshipAnchorAuditQueuedRef.current = false;
        await runAudit(
          relationshipAnchorAuditGenerationRef.current,
          coachId,
        );
        return;
      }
      if (
        generation === relationshipAnchorAuditGenerationRef.current &&
        relationshipLookupFailed &&
        retryAttempt < 1
      ) {
        relationshipAnchorAuditRetryTimeoutRef.current = window.setTimeout(() => {
          relationshipAnchorAuditRetryTimeoutRef.current = null;
          if (
            relationshipAnchorAuditMountedRef.current &&
            relationshipAnchorAuditCoachIdRef.current === coachId &&
            generation === relationshipAnchorAuditGenerationRef.current
          ) {
            void runAudit(generation, coachId, retryAttempt + 1);
          }
        }, 1_000);
        return;
      }
      if (
        relationshipAnchorAuditMountedRef.current &&
        generation === relationshipAnchorAuditGenerationRef.current &&
        !relationshipLookupFailed
      ) {
        setRelationshipAnchorAuditVerifiedCoachId(coachId);
        setIsRelationshipAnchorAuditPending(false);
      }
    };

    void runAudit(requestedGeneration, auditCoachId);
  }, [setActiveThreadId, setThreads, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const requestVisibleRelationshipAudit = () => {
      if (document.visibilityState !== "visible") return;
      requestRelationshipAnchorAudit();
    };
    window.addEventListener("focus", requestVisibleRelationshipAudit);
    document.addEventListener(
      "visibilitychange",
      requestVisibleRelationshipAudit,
    );
    return () => {
      window.removeEventListener("focus", requestVisibleRelationshipAudit);
      document.removeEventListener(
        "visibilitychange",
        requestVisibleRelationshipAudit,
      );
    };
  }, [requestRelationshipAnchorAudit, user?.id]);

  useEffect(() => {
    if (
      !user?.id ||
      urlAction !== "coach-identity-builder" ||
      isCoachIdentityPhilosophyBuilder
    )
      return;
    let cancelled = false;

    const loadBuilderSession = async () => {
      const { data, error } = await (
        (supabase as unknown as { from: (table: string) => unknown }).from(
          "coach_identity_builder_sessions",
        ) as {
          select: (columns: string) => {
            eq: (
              column: string,
              value: string,
            ) => {
              in: (
                column: string,
                values: string[],
              ) => {
                order: (
                  column: string,
                  options: { ascending: boolean },
                ) => {
                  limit: (value: number) => {
                    maybeSingle: () => Promise<{
                      data: CoachIdentityBuilderSessionRow | null;
                      error: { message?: string } | null;
                    }>;
                  };
                };
              };
            };
          };
        }
      )
        .select("chat_session_id, status, updated_at")
        .eq("coach_id", user.id)
        .in("status", ["active", "completed"])
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;
      if (error || !data) {
        setHasResolvedBuilderResume(true);
        return;
      }

      const builderContextType = resolveCoachRouteThreadContextType({
        isCoachIdentityBuilder: true,
        resolvedUrlContextType,
        coachModeEnabled: coachModeAvailableForPresentation,
      });
      setThreads((prev) => {
        const nextThreads = prev.filter((thread) => {
          if (thread.id === data.chat_session_id) return true;
          if (
            activeThreadId &&
            thread.id === activeThreadId &&
            thread.contextType === builderContextType &&
            thread.leaderId === null
          ) {
            return false;
          }
          if (
            thread.contextType === builderContextType &&
            thread.leaderId === null &&
            thread.fixedTitle === COACH_IDENTITY_BUILDER_THREAD_TITLE
          ) {
            return false;
          }
          return !(
            thread.contextType === builderContextType &&
            thread.leaderId === null &&
            thread.title ===
              COACH_THREAD_PLACEHOLDER_TITLES[builderContextType] &&
            thread.preview.trim().length === 0
          );
        });
        if (nextThreads.some((thread) => thread.id === data.chat_session_id))
          return nextThreads;
        return [
          {
            id: data.chat_session_id,
            title: COACH_IDENTITY_BUILDER_THREAD_TITLE,
            preview:
              data.status === "completed"
                ? "Draft ready to review"
                : "Continue your coach identity draft",
            updatedAt: data.updated_at,
            contextType: builderContextType,
            leaderId: null,
            fixedTitle: COACH_IDENTITY_BUILDER_THREAD_TITLE,
          },
          ...nextThreads,
        ];
      });
      setActiveThreadId(data.chat_session_id);
      setHasTriggeredAutoPrompt(true);
      setHasResolvedBuilderResume(true);
    };

    void loadBuilderSession();

    return () => {
      cancelled = true;
    };
  }, [
    user?.id,
    urlAction,
    isCoachIdentityPhilosophyBuilder,
    resolvedUrlContextType,
    coachModeAvailableForPresentation,
    activeThreadId,
    setActiveThreadId,
    setThreads,
  ]);

  useEffect(() => {
    setHasResolvedBuilderResume(
      !isCoachIdentityBuilder || isCoachIdentityPhilosophyBuilder,
    );
  }, [isCoachIdentityBuilder, isCoachIdentityPhilosophyBuilder]);

  useEffect(() => {
    setBriefOpen(false);
    setHasTriggeredAutoPrompt(false);
    handledAutoPromptKeyRef.current = null;
  }, [
    urlLeaderId,
    urlAction,
    urlSource,
    urlSignalType,
    urlSignalLevel,
    urlSignals,
    resolvedUrlContextType,
    coachModeEnabled,
  ]);

  useEffect(() => {
    setSelectedWorkType(initialWorkType);
  }, [initialWorkType]);

  const manualRouteClearPendingRef = useRef(false);
  useEffect(() => {
    if (manualRouteClearPendingRef.current) {
      const stillHasManualRouteBinding = Boolean(
        contextualEntry ||
          urlLeaderId ||
          urlAction ||
          urlContextType ||
          urlPrompt ||
          urlTranscriptId ||
          urlProvenanceId ||
          urlFrom ||
          urlSignalType ||
          urlSignalLevel ||
          urlSignals,
      );
      if (!stillHasManualRouteBinding) {
        manualRouteClearPendingRef.current = false;
      }
      return;
    }
    if (contextualEntry) return;
    if (!threads.length) return;
    if (!urlLeaderId && !urlAction) return;
    const routeActiveThread = threads.find(
      (thread) => thread.id === activeThreadId,
    );
    if (
      !areFeatureFlagsResolving &&
      !coachModeEnabled &&
      !isCoachIdentityBuilder &&
      routeActiveThread?.contextType === "coach"
    ) {
      // The preservation effect owns this transition. Rebinding the route
      // first would reset the exact chat scope and cancel the close capture.
      return;
    }

    const targetContextType = resolveCoachRouteThreadContextType({
      isCoachIdentityBuilder,
      resolvedUrlContextType,
      coachModeEnabled: coachModeAvailableForPresentation,
    });
    const targetLeaderId =
      targetContextType === "leader" ? urlLeaderId || null : null;

    const matchingThread = threads.find(
      (thread) =>
        thread.contextType === targetContextType &&
        (!routeThreadFixedTitle ||
          thread.fixedTitle === routeThreadFixedTitle ||
          thread.title === routeThreadFixedTitle) &&
        (targetContextType === "global" || thread.leaderId === targetLeaderId),
    );

    if (matchingThread) {
      if (activeThreadId !== matchingThread.id) {
        setActiveThreadId(matchingThread.id);
      }
      return;
    }

    const nextThread = createThread(
      targetContextType,
      targetLeaderId,
      routeThreadFixedTitle,
    );
    setThreads((prev) => [nextThread, ...prev]);
    setActiveThreadId(nextThread.id);
  }, [
    threads,
    activeThreadId,
    contextualEntry,
    areFeatureFlagsResolving,
    coachModeEnabled,
    urlLeaderId,
    urlAction,
    isCoachIdentityBuilder,
    resolvedUrlContextType,
    coachModeAvailableForPresentation,
    urlContextType,
    urlPrompt,
    urlTranscriptId,
    urlProvenanceId,
    urlFrom,
    urlSignalType,
    urlSignalLevel,
    urlSignals,
    routeThreadFixedTitle,
    setActiveThreadId,
    setThreads,
  ]);

  const activeThread = useMemo(
    () => threads.find((thread) => thread.id === activeThreadId) || null,
    [threads, activeThreadId],
  );
  const inputScopeKey = [
    activeThread?.id || "no-thread",
    activeThread?.contextType || "no-context",
    activeThread?.leaderId || "no-leader",
    coachModeAvailableForPresentation ? "coach-mode-on" : "coach-mode-off",
  ].join("|");
  const priorInputScopeKeyRef = useRef(inputScopeKey);
  useLayoutEffect(() => {
    if (priorInputScopeKeyRef.current !== inputScopeKey) {
      setInputValue("");
    }
    priorInputScopeKeyRef.current = inputScopeKey;
  }, [inputScopeKey]);
  const {
    activeContextualEntry,
    fixedTitle: activeThreadFixedTitle,
    sourceLabel: activeThreadSourceLabel,
  } = useMemo(
    () =>
      resolveActiveVeeraThreadPresentation({
        activeThread,
        contextualEntry,
      }),
    [activeThread, contextualEntry],
  );

  useEffect(() => {
    if (!activeThread) return;
    setSelectedWorkType(
      normalizeCoachWorkType(activeThread.contextType, coachModeAvailableForPresentation),
    );
  }, [activeThread, coachModeAvailableForPresentation]);

  const activeLeader = useMemo(
    () => leaders?.find((leader) => leader.id === activeThread?.leaderId),
    [leaders, activeThread?.leaderId],
  );
  const activeContextType: CoachChatContextType = normalizeCoachWorkType(
    activeThread?.contextType || "global",
    coachModeAvailableForPresentation,
  );
  const isCoachModeFallbackTransition = Boolean(
    !areFeatureFlagsResolving
      && !coachModeEnabled
      && !isCoachIdentityBuilder
      && activeThread?.contextType === "coach",
  );
  const coachModeFallbackError = Boolean(
    isCoachModeFallbackTransition &&
      activeThread?.id === coachModeFallbackErrorThreadId,
  );
  const shouldRetainCoachChatScope = Boolean(
    activeThread?.contextType === "coach"
      && (areFeatureFlagsResolving || isCoachModeFallbackTransition),
  );
  const chatContextType: CoachChatContextType = isCoachIdentityBuilder || shouldRetainCoachChatScope
    ? "coach"
    : activeContextType;
  const leaderRelationshipScopeKey =
    chatContextType === "leader" && user?.id && activeThread?.leaderId
      ? `${user.id}:${activeThread.leaderId}`
      : null;
  const [leaderRelationshipScope, setLeaderRelationshipScope] =
    useState<LeaderRelationshipScope | null>(null);
  const [leaderRelationshipRefreshNonce, setLeaderRelationshipRefreshNonce] =
    useState(0);

  useEffect(() => {
    if (!leaderRelationshipScopeKey || !user?.id || !activeThread?.leaderId) return;
    let cancelled = false;
    const scopeKey = leaderRelationshipScopeKey;
    setLeaderRelationshipScope({
      scopeKey,
      status: "loading",
      relationshipRevision: null,
      relationshipCreatedAt: null,
    });

    const loadRelationshipScope = async () => {
      try {
        const { data, error } = await supabase
          .from("coach_leader_links")
          .select("relationship_revision, created_at")
          .eq("coach_id", user.id)
          .eq("leader_id", activeThread.leaderId)
          .maybeSingle();
        if (cancelled) return;
        const relationshipRevision = data?.relationship_revision?.trim() || null;
        const relationshipCreatedAt = data?.created_at?.trim() || null;
        setLeaderRelationshipScope({
          scopeKey,
          status: !error && relationshipRevision && relationshipCreatedAt ? "ready" : "unavailable",
          relationshipRevision: !error ? relationshipRevision : null,
          relationshipCreatedAt: !error ? relationshipCreatedAt : null,
        });
      } catch {
        if (!cancelled) {
          setLeaderRelationshipScope({
            scopeKey,
            status: "unavailable",
            relationshipRevision: null,
            relationshipCreatedAt: null,
          });
        }
      }
    };
    void loadRelationshipScope();

    return () => {
      cancelled = true;
    };
  }, [activeThread?.leaderId, leaderRelationshipRefreshNonce, leaderRelationshipScopeKey, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const scopeKey = leaderRelationshipScopeKey;
    const activeLeaderId =
      chatContextType === "leader" ? activeThread?.leaderId || null : null;
    const queuePortfolioRelationshipAudit = (options: {
      changedLeaderId: string | null;
      quarantineAllLeaderThreads: boolean;
    }) => {
      const { changedLeaderId, quarantineAllLeaderThreads } = options;
      setHasResolvedPersistedThreadHydration(false);
      setThreads((current) =>
        current.filter((thread) => {
          if (thread.contextType !== "leader") return true;
          if (quarantineAllLeaderThreads) return false;
          if (thread.id === activeThreadId) return true;
          return changedLeaderId
            ? thread.leaderId !== changedLeaderId
            : false;
        }),
      );
      setPortfolioRelationshipRefreshNonce((current) => current + 1);

      if (
        scopeKey &&
        (!changedLeaderId || changedLeaderId === activeLeaderId)
      ) {
        setLeaderRelationshipScope({
          scopeKey,
          status: "loading",
          relationshipRevision: null,
          relationshipCreatedAt: null,
        });
        setLeaderRelationshipRefreshNonce((current) => current + 1);
      }
    };
    const handleRelationshipUpsert = (payload: unknown) => {
      queuePortfolioRelationshipAudit({
        changedLeaderId: resolveChangedLeaderId(payload),
        quarantineAllLeaderThreads: false,
      });
    };
    const channel = supabase
      .channel(`coach-ai-relationships-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "coach_leader_links",
          filter: `coach_id=eq.${user.id}`,
        },
        handleRelationshipUpsert,
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "coach_leader_links",
          filter: `coach_id=eq.${user.id}`,
        },
        handleRelationshipUpsert,
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "coach_leader_links",
        },
        () => {
          // Supabase does not apply Postgres Changes filters to DELETE events,
          // and deleted rows may expose only their primary key. Do not trust
          // the payload to identify a coach or leader; quarantine all private
          // leader previews and rebuild them from current authorized rows.
          queuePortfolioRelationshipAudit({
            changedLeaderId: null,
            quarantineAllLeaderThreads: true,
          });
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          requestRelationshipAnchorAudit();
        }
      });
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [
    activeThread?.leaderId,
    activeThreadId,
    chatContextType,
    leaderRelationshipScopeKey,
    requestRelationshipAnchorAudit,
    setHasResolvedPersistedThreadHydration,
    setThreads,
    user?.id,
  ]);

  const relationshipRevision =
    leaderRelationshipScopeKey
      && leaderRelationshipScope?.scopeKey === leaderRelationshipScopeKey
      && leaderRelationshipScope.status === "ready"
      ? leaderRelationshipScope.relationshipRevision || undefined
      : undefined;
  const isLeaderRelationshipScopeReady =
    chatContextType !== "leader" || Boolean(relationshipRevision);
  const relationshipCreatedAt =
    relationshipRevision ? leaderRelationshipScope?.relationshipCreatedAt || undefined : undefined;

  useEffect(() => {
    if (
      chatContextType !== "leader" ||
      !relationshipCreatedAt ||
      !activeThread?.leaderId ||
      activeThread.contextType !== "leader"
    ) return;
    const relationshipStartedAt = Date.parse(relationshipCreatedAt);
    const threadUpdatedAt = Date.parse(activeThread.updatedAt);
    if (
      !Number.isFinite(relationshipStartedAt) ||
      !Number.isFinite(threadUpdatedAt) ||
      threadUpdatedAt >= relationshipStartedAt
    ) return;

    const replacementThread = createThread(
      "leader",
      activeThread.leaderId,
      activeThread.fixedTitle || null,
    );
    hideThread(activeThread.id);
    setThreads((current) => [
      replacementThread,
      ...current.filter((thread) => thread.id !== activeThread.id),
    ]);
    setActiveThreadId(replacementThread.id);
  }, [
    activeThread,
    chatContextType,
    relationshipCreatedAt,
    hideThread,
    setActiveThreadId,
    setThreads,
  ]);
  const compatibleChatThreadId =
    activeThread?.contextType === chatContextType && isLeaderRelationshipScopeReady
      ? activeThread.id
      : undefined;
  const isCoachThreadAwaitingFeatureFlag = Boolean(
    activeThread?.contextType === "coach" && areFeatureFlagsResolving,
  );
  const isCoachScopeTransitionLocked = Boolean(
    isCoachThreadAwaitingFeatureFlag || isCoachModeFallbackTransition,
  );
  const isChatThreadContextCompatible = Boolean(compatibleChatThreadId)
    && isLeaderRelationshipScopeReady
    && !isCoachThreadAwaitingFeatureFlag
    && !isCoachModeFallbackTransition;
  const {
    coachMemory,
    coachRuntime,
    coachLens,
    coachTwin,
    coachAiPageContextData,
    coachRuntimeActions,
    isError: isRuntimeError,
  } = useCoachPortfolioRuntime({
    coachId: user?.id,
    leaderId:
      chatContextType === "leader" ? activeThread?.leaderId || null : null,
    contextType:
      chatContextType === "leader"
        ? "leader"
        : chatContextType === "coach"
          ? "session"
          : "global",
    activeLeader: activeLeader || null,
    enabled: Boolean(user?.id),
  });

  const pageContext = useMemo(
    () => ({
      pageName: "coach-ai",
      leaderId:
        chatContextType === "leader"
          ? activeThread?.leaderId || undefined
          : undefined,
      relationshipRevision:
        chatContextType === "leader" ? relationshipRevision : undefined,
      relationshipCreatedAt:
        chatContextType === "leader" ? relationshipCreatedAt : undefined,
      data: {
        ...coachAiPageContextData,
        contextualEntry: activeContextualEntry,
        activeTranscriptUploadId:
          chatContextType === "coach" && urlAction === "coach-transcript"
            ? urlTranscriptId || null
            : null,
        activeTranscriptProvenanceId:
          chatContextType === "coach" && urlAction === "coach-transcript"
            ? urlProvenanceId || null
            : null,
        coachIdentityBuilderMode: isCoachIdentityBuilder,
        coachIdentityBuilderSource: isCoachIdentityBuilder
          ? urlSource || "coaching-system"
          : null,
        coachIdentityBuilderTemplate: isCoachIdentityBuilder
          ? buildCoachIdentityTemplate()
          : null,
      },
    }),
    [
      chatContextType,
      activeThread?.leaderId,
      relationshipRevision,
      relationshipCreatedAt,
      activeContextualEntry,
      coachAiPageContextData,
      isCoachIdentityBuilder,
      urlSource,
      urlAction,
      urlTranscriptId,
      urlProvenanceId,
    ],
  );

  const {
    messages: rawMessages,
    harnessPayload: rawHarnessPayload,
    isLoading: rawIsLoading,
    isScopeReady: isCoachChatScopeReady = true,
    sendMessage: rawSendMessage,
    clearChat,
    closeConversation,
    captureCloseInBackground,
    messagesEndRef,
    isStreaming: rawIsStreaming,
    streamingContent: rawStreamingContent,
  } = useCoachChat(
    pageContext,
    chatContextType,
    historyScope,
    compatibleChatThreadId,
    {
      transcriptUploadId:
        chatContextType === "coach" && urlAction === "coach-transcript"
          ? urlTranscriptId || null
          : null,
    },
  );
  const isChatThreadCompatible =
    isChatThreadContextCompatible
    && isCoachChatScopeReady
    && hasResolvedPersistedThreadHydration
    && !(
      shouldQuarantineLeaderRelationshipThreads &&
      chatContextType === "leader"
    );
  const messages = useMemo(
    () => (isChatThreadCompatible ? rawMessages : []),
    [isChatThreadCompatible, rawMessages],
  );
  const harnessPayload = isChatThreadCompatible ? rawHarnessPayload : null;
  const isLoading =
    rawIsLoading || (!isChatThreadCompatible && !coachModeFallbackError);
  const isStreaming = isChatThreadCompatible ? rawIsStreaming : false;
  const streamingContent = isChatThreadCompatible ? rawStreamingContent : "";
  const sendMessage = useCallback(
    async (...args: Parameters<typeof rawSendMessage>) => {
      if (!isChatThreadCompatible || premiumActivityUnavailable) return;
      await rawSendMessage(...args);
    },
    [isChatThreadCompatible, premiumActivityUnavailable, rawSendMessage],
  );

  useEffect(() => {
    if (!premiumActivityLocked || lockedBotViewedRef.current) return;
    lockedBotViewedRef.current = true;
    captureTrialEvent("locked_bot_viewed", {
      role: "coach",
      trialState: trialAccess.presentation.state,
      daysRemaining: trialAccess.presentation.daysRemaining,
      surface: "coach_veera",
    });
  }, [
    premiumActivityLocked,
    trialAccess.presentation.daysRemaining,
    trialAccess.presentation.state,
  ]);
  const coachModeFallbackStartedRef = useRef<string | null>(null);
  const retryCoachModeFallback = useCallback(() => {
    if (
      !activeThread ||
      activeThread.id !== coachModeFallbackErrorThreadId
    ) return;
    coachModeFallbackStartedRef.current = null;
    setCoachModeFallbackErrorThreadId(null);
    setCoachModeFallbackRetryNonce((current) => current + 1);
  }, [activeThread, coachModeFallbackErrorThreadId]);

  useEffect(() => {
    if (!isCoachModeFallbackTransition || !activeThread) {
      coachModeFallbackStartedRef.current = null;
      setCoachModeFallbackErrorThreadId((current) =>
        current === null ? current : null,
      );
      return;
    }
    if (
      !hasResolvedPersistedThreadHydration ||
      !isCoachChatScopeReady ||
      rawIsLoading ||
      rawIsStreaming
    ) return;

    const transitionKey = activeThread.id;
    if (coachModeFallbackStartedRef.current === transitionKey) return;
    coachModeFallbackStartedRef.current = transitionKey;
    setCoachModeFallbackErrorThreadId(null);

    void (async () => {
      if (rawMessages.some((message) => message.role === "user")) {
        const closeCapture = await closeConversation({
          actions: ["journal"],
          leaderId: null,
          transcriptUploadId:
            urlAction === "coach-transcript" ? urlTranscriptId || null : null,
          coachCallId: null,
          silent: true,
        });
        if (
          !closeCapture
          || (
            closeCapture.saved === false
            && closeCapture.error !== "empty_summary"
            && closeCapture.error !== "trial_expired"
            && closeCapture.error !== "session_unattested"
          )
        ) {
          setCoachModeFallbackErrorThreadId(transitionKey);
          return;
        }
        if (closeCapture.error === "trial_expired") {
          toast.info("Your history remains. New Intelligence requires a plan.");
        }
        if (closeCapture.error === "session_unattested") {
          toast.info(
            "This conversation remains in your history, but it was not added to Coach Memory.",
          );
        }
      }
      if (coachModeFallbackStartedRef.current !== transitionKey) return;

      const existingGlobalThread = threads.find(
        (thread) => thread.contextType === "global" && thread.leaderId === null,
      );
      const nextThread = existingGlobalThread ?? createThread("global", null);
      if (!existingGlobalThread) {
        setThreads((current) => [nextThread, ...current]);
      }
      setActiveThreadId(nextThread.id);
      setSelectedWorkType("global");
      setCoachModeFallbackErrorThreadId(null);
    })();
  }, [
    activeThread,
    closeConversation,
    hasResolvedPersistedThreadHydration,
    isCoachChatScopeReady,
    isCoachModeFallbackTransition,
    rawIsLoading,
    rawIsStreaming,
    rawMessages,
    coachModeFallbackRetryNonce,
    setActiveThreadId,
    setThreads,
    threads,
    urlAction,
    urlTranscriptId,
  ]);
  const lifecycleCloseCaptureRef = useRef({
    key: "",
    activeThreadId: null as string | null,
    chatContextType: "global" as CoachChatContextType,
    messageCount: 0,
    transcriptUploadId: null as string | null,
    captureCloseInBackground,
  });
  const lastLifecycleCloseCaptureKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const hasVerifiedRawCoachScope = Boolean(
      activeThread?.contextType === "coach" &&
        hasResolvedPersistedThreadHydration &&
        isCoachChatScopeReady,
    );
    const lifecycleMessages = hasVerifiedRawCoachScope ? rawMessages : messages;
    const lifecycleChatContextType: CoachChatContextType = hasVerifiedRawCoachScope
      ? "coach"
      : chatContextType;
    const latestMessage = lifecycleMessages[lifecycleMessages.length - 1];
    lifecycleCloseCaptureRef.current = {
      key: [
        user?.id || "no-user",
        activeThreadId || "no-thread",
        lifecycleChatContextType,
        lifecycleMessages.length,
        latestMessage?.id || "",
        latestMessage?.content || "",
      ].join(":"),
      activeThreadId,
      chatContextType: lifecycleChatContextType,
      messageCount: lifecycleMessages.length,
      transcriptUploadId:
        lifecycleChatContextType === "coach" && urlAction === "coach-transcript"
          ? urlTranscriptId || null
          : null,
      captureCloseInBackground,
    };
  }, [
    activeThreadId,
    activeThread?.contextType,
    captureCloseInBackground,
    chatContextType,
    hasResolvedPersistedThreadHydration,
    isCoachChatScopeReady,
    messages,
    rawMessages,
    user?.id,
    urlAction,
    urlTranscriptId,
  ]);

  const captureActiveCoachThreadInBackground = useCallback(() => {
    const state = lifecycleCloseCaptureRef.current;
    if (state.chatContextType !== "coach" || state.messageCount === 0) return false;
    if (lastLifecycleCloseCaptureKeyRef.current === state.key) return false;
    const dispatched = state.captureCloseInBackground({
      actions: ["journal"],
      leaderId: null,
      transcriptUploadId: state.transcriptUploadId,
      coachCallId: null,
    });
    if (dispatched) {
      lastLifecycleCloseCaptureKeyRef.current = state.key;
    }
    return dispatched;
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        captureActiveCoachThreadInBackground();
      }
    };

    window.addEventListener("pagehide", captureActiveCoachThreadInBackground);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      captureActiveCoachThreadInBackground();
      window.removeEventListener("pagehide", captureActiveCoachThreadInBackground);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [captureActiveCoachThreadInBackground]);

  const latestAssistantMessage = useMemo(
    () =>
      [...messages].reverse().find((message) => message.role === "assistant")
        ?.content || null,
    [messages],
  );

  const coachIdentityDraftPreview = useMemo(() => {
    if (!isCoachIdentityBuilder || !latestAssistantMessage) return null;
    const parsed = parseCoachIdentityBuilderDraft(latestAssistantMessage);
    return parsed.ok ? parsed : null;
  }, [isCoachIdentityBuilder, latestAssistantMessage]);

  useEffect(() => {
    if (!activeThreadId || messages.length === 0) return;
    const currentMessageSignature = messages
      .map((message) => `${message.id}:${message.role}:${message.content}`)
      .join("||");
    const previousThreadMessageState = lastThreadMessageSignatureRef.current;
    const isThreadSwitchWithStaleMessages =
      previousThreadMessageState.threadId !== null &&
      previousThreadMessageState.threadId !== activeThreadId &&
      previousThreadMessageState.signature === currentMessageSignature;

    lastThreadMessageSignatureRef.current = {
      threadId: activeThreadId,
      signature: currentMessageSignature,
    };

    if (isThreadSwitchWithStaleMessages) return;

    const latestMessage = messages[messages.length - 1];
    const firstUserMessage = messages.find(
      (message) => message.role === "user",
    )?.content;
    const titleSeed =
      firstUserMessage ||
      (isCoachIdentityBuilder ? "My Coaching System" : "New conversation");
    const title =
      activeThreadFixedTitle ||
      buildGeneratedVeeraThreadTitle(titleSeed, "New conversation", 42);
    const preview = latestMessage?.content || "";

    setThreads((prev) =>
      prev
        .map((thread) => {
          if (thread.id !== activeThreadId) return thread;
          if (thread.fixedTitle) {
            return {
              ...thread,
              title: thread.fixedTitle,
              preview:
                preview.length > 86 ? `${preview.slice(0, 86)}...` : preview,
              updatedAt: new Date().toISOString(),
            };
          }
          return {
            ...thread,
            title,
            preview:
              preview.length > 86 ? `${preview.slice(0, 86)}...` : preview,
            updatedAt: new Date().toISOString(),
          };
        })
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        ),
    );
  }, [
    activeThreadFixedTitle,
    messages,
    activeThreadId,
    isCoachIdentityBuilder,
    setThreads,
  ]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [activeThreadId]);

  const autoPromptRouteKey = useMemo(() => {
    if (contextualEntry || !urlAction) return null;
    return JSON.stringify({
      action: urlAction,
      source: urlSource ?? null,
      contextType: resolvedUrlContextType,
      leaderId: urlLeaderId ?? null,
      prompt: urlPrompt ?? null,
      transcriptId: urlTranscriptId ?? null,
      provenanceId: urlProvenanceId ?? null,
      signalType: urlSignalType ?? null,
      signalLevel: urlSignalLevel ?? null,
      signals: urlSignals ?? null,
    });
  }, [
    contextualEntry,
    resolvedUrlContextType,
    urlAction,
    urlSource,
    urlLeaderId,
    urlPrompt,
    urlTranscriptId,
    urlProvenanceId,
    urlSignalType,
    urlSignalLevel,
    urlSignals,
  ]);

  useEffect(() => {
    if (contextualEntry) return;
    if (
      !urlAction ||
      !activeThreadId ||
      !isChatThreadCompatible ||
      hasTriggeredAutoPrompt ||
      isLoading ||
      premiumActivityUnavailable ||
      !autoPromptRouteKey
    )
      return;
    if (handledAutoPromptKeyRef.current === autoPromptRouteKey) return;
    if (
      urlAction === "coach-identity-builder" &&
      (!hasResolvedBuilderResume || !hasResolvedPersistedThreadHydration)
    ) {
      return;
    }
    if (
      resolvedUrlContextType === "leader" &&
      urlLeaderId &&
      (activeThread?.contextType !== "leader" ||
        activeThread.leaderId !== urlLeaderId)
    ) {
      return;
    }
    if (
      resolvedUrlContextType === "leader" &&
      routeThreadFixedTitle &&
      activeThread?.fixedTitle !== routeThreadFixedTitle &&
      activeThread?.title !== routeThreadFixedTitle
    ) {
      return;
    }
    if (
      resolvedUrlContextType === "coach" &&
      activeThread?.contextType !== "coach"
    )
      return;

    handledAutoPromptKeyRef.current = autoPromptRouteKey;
    setHasTriggeredAutoPrompt(true);

    if (urlAction === "draft-message") {
      void sendMessage(
        "Draft a message I can send to this leader. Include a brief summary of recent moments and 2 coaching questions.",
      );
      return;
    }

    if (urlAction === "sequencing") {
      void sendMessage(
        "Help me think through sequencing for this leader. What patterns from recent moments are present? What might they mean?",
      );
      return;
    }

    if (urlAction === "signal-interpret") {
      const signalList =
        urlSignals
          ?.split("|")
          .map((part) => part.trim())
          .filter(Boolean) || [];
      void sendMessage(
        coachRuntimeActions.buildSignalInterpretPrompt({
          signalType: urlSignalType,
          signalLevel: urlSignalLevel,
          signals: signalList,
        }),
      );
      return;
    }

    if (urlAction === "coach-transcript") {
      const seededPrompt =
        urlPrompt ||
        "Coach me on improving my approach from this session evidence.";
      const evidenceBits = [
        urlTranscriptId ? `Transcript ID: ${urlTranscriptId}` : null,
        urlProvenanceId ? `Provenance ID: ${urlProvenanceId}` : null,
      ].filter(Boolean);
      const evidenceContext =
        evidenceBits.length > 0
          ? `\n\nEvidence context:\n${evidenceBits.join("\n")}`
          : "";
      void sendMessage(`${seededPrompt}${evidenceContext}`);
      return;
    }

    if (urlAction === "coach-identity-builder") {
      const isExistingBuilderThread =
        Boolean(routeThreadFixedTitle) &&
        (activeThread?.fixedTitle === routeThreadFixedTitle ||
          activeThread?.title === routeThreadFixedTitle);
      const hasExistingBuilderHistory =
        isExistingBuilderThread &&
        (messages.length > 0 || Boolean(activeThread?.preview.trim().length));
      if (hasExistingBuilderHistory) return;
      const builderPrompt = isCoachIdentityPhilosophyBuilder
        ? COACH_IDENTITY_PHILOSOPHY_PROMPT
        : COACH_IDENTITY_BUILDER_PROMPT;
      void sendMessage(urlPrompt || builderPrompt, {
        suppressUserEcho: true,
      });
    }
  }, [
    resolvedUrlContextType,
    urlAction,
    urlSource,
    urlPrompt,
    urlTranscriptId,
    urlProvenanceId,
    urlLeaderId,
    activeThreadId,
    activeThread?.contextType,
    activeThread?.fixedTitle,
    activeThread?.leaderId,
    activeThread?.preview,
    activeThread?.title,
    routeThreadFixedTitle,
    hasTriggeredAutoPrompt,
    hasResolvedBuilderResume,
    hasResolvedPersistedThreadHydration,
    isCoachIdentityPhilosophyBuilder,
    isLoading,
    premiumActivityUnavailable,
    messages.length,
    sendMessage,
    urlSignalType,
    urlSignalLevel,
    urlSignals,
    coachRuntimeActions,
    contextualEntry,
    autoPromptRouteKey,
    isChatThreadCompatible,
  ]);

  const quickPrompts = isCoachIdentityPhilosophyBuilder
    ? COACH_IDENTITY_PHILOSOPHY_PROMPTS
    : activeContextType === "leader"
      ? LEADER_PROMPTS
      : activeContextType === "coach"
        ? COACH_PROMPTS
        : GLOBAL_PROMPTS;
  const coachLensStatus = useMemo(() => {
    const hasLens = Boolean(coachLens);
    const hasTwin = Boolean(coachTwin);
    const hasFrameworks =
      Array.isArray(coachLens?.frameworks) && coachLens.frameworks.length > 0;
    const lensTimestamps = coachLens as CoachLensTimestampSource | null;
    return deriveCoachLensStatus({
      hasLens,
      hasTwin,
      hasFrameworks,
      lensUpdatedAt:
        lensTimestamps?.coach_lens_updated_at
        ?? lensTimestamps?.updated_at
        ?? lensTimestamps?.updatedAt
        ?? undefined,
      loadFailed: isRuntimeError,
    });
  }, [coachLens, coachTwin, isRuntimeError]);

  const latestCoachInsight = useMemo<CoachInsight | null>(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role === "assistant" && message.coachInsight)
        return message.coachInsight;
    }
    return null;
  }, [messages]);

  // Surface tension toasts when harness detects cross-pack conflicts
  const lastTensionCountRef = useRef(0);
  useEffect(() => {
    if (!harnessPayload || harnessPayload.tensions.length === 0) return;
    if (harnessPayload.tensions.length === lastTensionCountRef.current) return;
    lastTensionCountRef.current = harnessPayload.tensions.length;
    for (const tension of harnessPayload.tensions) {
      toast.info(tension.description, {
        duration: 6000,
      });
    }
  }, [harnessPayload]);

  const coachTranscriptArrivalTitle =
    urlFrom === "post-meeting-review"
      ? "Opened from post-meeting review"
      : "Coach transcript context active";
  const coachTranscriptArrivalBody = [
    urlTranscriptId
      ? `Transcript ${urlTranscriptId} is active for this coach thread.`
      : "This coach thread is seeded from a transcript review.",
    urlProvenanceId
      ? `Provenance ${urlProvenanceId} will travel with follow-up questions.`
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  const historyScopeLabel = useMemo(
    () =>
      COACH_AI_HISTORY_SCOPES.find((scope) => scope.value === historyScope)
        ?.label || "90d",
    [historyScope],
  );
  const leaderNameById = useMemo(
    () => new Map((leaders || []).map((leader) => [leader.id, leader.name])),
    [leaders],
  );
  const sidebarThreads = useMemo<CoachSidebarThread[]>(
    () =>
      threads
        .filter(
          (thread) =>
            (coachModeAvailableForPresentation || thread.contextType !== "coach") &&
            (!shouldQuarantineLeaderRelationshipThreads || thread.contextType !== "leader") &&
            normalizeCoachWorkType(thread.contextType, coachModeAvailableForPresentation) ===
            selectedWorkType,
        )
        .map((thread) => ({
          id: thread.id,
          title: thread.title,
          preview: thread.preview,
          updatedAt: thread.updatedAt,
          tagLabel:
            thread.contextType === "leader"
              ? leaderNameById.get(thread.leaderId || "")
              : undefined,
        })),
    [
      coachModeAvailableForPresentation,
      leaderNameById,
      selectedWorkType,
      shouldQuarantineLeaderRelationshipThreads,
      threads,
    ],
  );
  const filteredLeaders = useMemo(() => {
    const query = leaderSearch.trim().toLowerCase();
    if (!query) return leaders || [];
    return (leaders || []).filter((leader) =>
      leader.name.toLowerCase().includes(query),
    );
  }, [leaderSearch, leaders]);
  const subtitle =
    activeContextType === "leader"
      ? `Leader analysis \u2014 ${historyScopeLabel}`
      : `${CONTEXT_SUBTITLES[activeContextType]} \u2014 ${historyScopeLabel}`;
  const contextTitle = activeThreadFixedTitle
    ? activeThreadFixedTitle
    : activeContextType === "leader"
      ? activeLeader?.name || WORK_TYPE_LABELS.leader
      : WORK_TYPE_LABELS[activeContextType];
  const newThreadLabel = NEW_THREAD_LABELS[selectedWorkType];
  const clearManualRouteBinding = useCallback(() => {
    if (
      !urlLeaderId &&
      !urlAction &&
      !urlContextType &&
      !urlPrompt &&
      !urlTranscriptId &&
      !urlProvenanceId &&
      !urlFrom &&
      !urlSignalType &&
      !urlSignalLevel &&
      !urlSignals &&
      !contextualEntry
    ) {
      return;
    }
    manualRouteClearPendingRef.current = true;
    navigate("/coach/veera", { replace: true });
  }, [
    contextualEntry,
    navigate,
    urlAction,
    urlContextType,
    urlFrom,
    urlLeaderId,
    urlPrompt,
    urlProvenanceId,
    urlSignalLevel,
    urlSignalType,
    urlSignals,
    urlTranscriptId,
  ]);

  // Coach-practice conversations are only persisted to coach memory when a
  // close capture runs. Every path that leaves the active coach-mode thread
  // must capture first, or the conversation is silently lost. Captures are
  // silent (no confirmation message) because they run during navigation, and
  // an appended message would land in the target thread's state.
  //
  // Identity-builder threads are captured too: isCoachIdentityBuilder is URL
  // state that stays true for the whole page visit, so skipping on it would
  // drop every post-builder coach conversation. The close-capture upsert is
  // session-idempotent, so re-captures merge rather than duplicate.
  type CloseCaptureExitOutcome =
    | "captured"
    | "entitlement_blocked"
    | "history_retained"
    | "failed"
    | "skipped_busy"
    | "not_applicable";

  const captureCoachCloseIfNeeded = async (): Promise<CloseCaptureExitOutcome> => {
    if (activeThread?.contextType !== "coach") return "not_applicable";
    // Until scope/hydration is ready, messages is deliberately filtered to [].
    // Loading must block an exit before that empty view can skip capture.
    if (isLoading || isStreaming) {
      return "skipped_busy";
    }
    if (!messages.some((message) => message.role === "user")) return "not_applicable";
    const closeCapture = await closeConversation({
      actions: ["journal"],
      leaderId: null,
      transcriptUploadId:
        urlAction === "coach-transcript" ? urlTranscriptId || null : null,
      coachCallId: null,
      silent: true,
    });
    if (!closeCapture) return "failed";
    if (closeCapture.saved === false && closeCapture.error === "trial_expired") {
      toast.info("Your history remains. New Intelligence requires a plan.");
      return "entitlement_blocked";
    }
    if (closeCapture.saved === false && closeCapture.error === "session_unattested") {
      toast.info(
        "This conversation remains in your history, but it was not added to Coach Memory.",
      );
      return "history_retained";
    }
    if (closeCapture.saved === false && closeCapture.error !== "empty_summary") {
      return "failed";
    }
    return "captured";
  };

  // Exits from coach-practice threads must wait until the close capture is safe.
  const captureCoachCloseBeforeSwitch = async (): Promise<boolean> => {
    const outcome = await captureCoachCloseIfNeeded();
    if (outcome === "skipped_busy") {
      toast.info(
        "Veera is still responding — wait for the reply before leaving this conversation.",
      );
      return false;
    }
    if (outcome === "failed") {
      return false;
    }
    return true;
  };

  const switchToExistingOrCreateThread = async (
    workType: CoachChatContextType,
    onBeforeSwitch?: () => void,
  ): Promise<boolean> => {
    if (workType === "coach" && !coachModeEnabled) {
      toast.error("Coach mode is not enabled.");
      return false;
    }

    const existing = threads.find(
      (thread) => thread.contextType === workType,
    );
    if (existing?.id === activeThreadId) {
      return false;
    }

    // Capture whenever the resolved target differs from the active thread —
    // including coach→coach switches between two coach threads.
    const canSwitch = await captureCoachCloseBeforeSwitch();
    if (!canSwitch) return false;

    onBeforeSwitch?.();

    if (existing) {
      setActiveThreadId(existing.id);
      setSelectedWorkType(workType);
      return true;
    }

    const nextThread = createThread(workType, null);
    setThreads((prev) => [nextThread, ...prev]);
    setActiveThreadId(nextThread.id);
    setSelectedWorkType(workType);
    return true;
  };

  const handleWorkTypeSelect = async (workType: CoachChatContextType) => {
    if (isCoachScopeTransitionLocked) return;
    if (activeThread?.contextType === workType) return;
    if (workType === "leader") {
      const existingLeaderThread = threads.find(
        (thread) => thread.contextType === "leader",
      );
      if (existingLeaderThread) {
        const canSwitch = await captureCoachCloseBeforeSwitch();
        if (!canSwitch) return;
        clearManualRouteBinding();
        setActiveThreadId(existingLeaderThread.id);
        setSelectedWorkType("leader");
        return;
      }
      const canSwitch = await captureCoachCloseBeforeSwitch();
      if (!canSwitch) return;
      clearManualRouteBinding();
      setLeaderSearch("");
      setLeaderPickerOpen(true);
      return;
    }

    await switchToExistingOrCreateThread(workType, clearManualRouteBinding);
  };

  const handleThreadSelect = async (threadId: string) => {
    if (isCoachScopeTransitionLocked) return;
    if (threadId === activeThreadId) return;
    const nextThread = threads.find((thread) => thread.id === threadId);
    if (nextThread?.contextType === "coach" && !coachModeEnabled) {
      return;
    }
    const canSwitch = await captureCoachCloseBeforeSwitch();
    if (!canSwitch) return;
    clearManualRouteBinding();
    setActiveThreadId(threadId);
    if (nextThread) {
      setSelectedWorkType(
        normalizeCoachWorkType(nextThread.contextType, coachModeEnabled),
      );
    }
  };

  const handleNewThread = async () => {
    if (isCoachScopeTransitionLocked) return;
    if (selectedWorkType === "leader") {
      const canSwitch = await captureCoachCloseBeforeSwitch();
      if (!canSwitch) return;
      setLeaderSearch("");
      setLeaderPickerOpen(true);
      return;
    }

    if (selectedWorkType === "coach" && !coachModeEnabled) {
      toast.error("Coach mode is not enabled.");
      return;
    }

    const canSwitch = await captureCoachCloseBeforeSwitch();
    if (!canSwitch) return;
    clearManualRouteBinding();
    const nextThread: CoachThread = {
      ...createThread(selectedWorkType, null),
      creationOrigin: "user",
    };
    setThreads((prev) => [nextThread, ...prev]);
    setActiveThreadId(nextThread.id);
  };

  const handleLeaderSelection = async (leaderId: string) => {
    if (isCoachScopeTransitionLocked) return;
    const canSwitch = await captureCoachCloseBeforeSwitch();
    if (!canSwitch) return;
    clearManualRouteBinding();
    const nextThread = createThread("leader", leaderId);
    setThreads((prev) => [nextThread, ...prev]);
    setActiveThreadId(nextThread.id);
    setSelectedWorkType("leader");
    setLeaderPickerOpen(false);
    setLeaderSearch("");
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !inputValue.trim() ||
      isLoading ||
      !isChatThreadCompatible ||
      premiumActivityUnavailable
    )
      return;
    sendMessage(inputValue.trim());
    setInputValue("");
  };

  const handleDeleteConversation = async () => {
    if (!activeThreadId || !isChatThreadCompatible) return;

    // Unlike a switch, delete destroys the thread — "saved next time you
    // leave it" would be a lie here, so a busy skip must block the delete
    // rather than proceed past it.
    const captureOutcome = await captureCoachCloseIfNeeded();
    if (captureOutcome === "skipped_busy") {
      toast.info("Veera is still responding — wait for the reply before deleting this conversation.");
      return;
    }
    if (captureOutcome === "failed") {
      return;
    }

    hideThread(activeThreadId);
    clearChat();

    const remainingThreads = threads.filter(
      (thread) => thread.id !== activeThreadId,
    );
    const nextThread =
      remainingThreads[0] ??
      createThread(
        activeContextType,
        activeContextType === "leader" ? activeThread?.leaderId || null : null,
      );
    const nextThreads =
      remainingThreads.length > 0 ? remainingThreads : [nextThread];

    setThreads(nextThreads);
    setActiveThreadId(nextThread.id);
    setInputValue("");
  };

  const handleUseCoachIdentityDraft = () => {
    if (!latestAssistantMessage) return;
    saveCoachIdentityBuilderDraft(latestAssistantMessage);
    navigate(buildCoachIdentityGuidedFormRoute());
  };

  const handleRequestCoachIdentityDraft = () => {
    if (isLoading || isStreaming || premiumActivityUnavailable) return;
    void sendMessage(
      isCoachIdentityPhilosophyBuilder
        ? COACH_IDENTITY_PHILOSOPHY_REVIEW_DRAFT_PROMPT
        : COACH_IDENTITY_SYSTEM_REVIEW_DRAFT_PROMPT,
    );
  };

  const handleOpenCoachIdentityMaterials = () => {
    navigate(buildCoachIdentityGuidedFormRoute("materials"));
  };

  const handleViewPlans = () => {
    captureTrialEvent("locked_bot_pricing_clicked", {
      role: "coach",
      trialState: trialAccess.presentation.state,
      daysRemaining: trialAccess.presentation.daysRemaining,
      surface: "coach_veera",
    });
    const params = new URLSearchParams({ trial_plan: "intelligence_solo" });
    if (trialAccess.primaryLeaderId) {
      params.set("trial_leader", trialAccess.primaryLeaderId);
    }
    navigate(`/coach/settings?${params.toString()}#leaders`);
  };

  return {
    activeContextType,
    activeLeader,
    activeThreadId,
    activeThreadSourceLabel,
    briefOpen,
    coachModeFallbackError,
    isCoachScopeTransitionLocked,
    coachIdentityDraftPreview,
    coachMemory,
    coachRuntime,
    contextTitle,
    filteredLeaders,
    handleDeleteConversation,
    handleLeaderSelection,
    handleNewThread,
    handleOpenCoachIdentityMaterials,
    handleRequestCoachIdentityDraft,
    handleSubmit,
    handleThreadSelect,
    handleUseCoachIdentityDraft,
    handleViewPlans,
    handleWorkTypeSelect,
    harnessPayload,
    historyScope,
    historyScopeLabel,
    coachLensStatus,
    inputRef,
    inputValue,
    isCoachIdentityBuilder,
    isCoachIdentityPhilosophyBuilder,
    isLoading,
    isStreaming,
    premiumActivityChecking,
    premiumActivityExpired,
    premiumActivityLocked,
    latestCoachInsight,
    leaderPickerOpen,
    leaderSearch,
    messages,
    messagesEndRef,
    mobileSidebarOpen,
    newThreadLabel,
    quickPrompts,
    retryCoachModeFallback,
    selectedWorkType,
    sendMessage,
    setBriefOpen,
    setHistoryScope,
    setInputValue,
    setLeaderPickerOpen,
    setLeaderSearch,
    setMobileSidebarOpen,
    sidebarThreads,
    streamingContent,
    subtitle,
    coachTranscriptArrivalBody,
    coachTranscriptArrivalTitle,
    urlAction,
  };
}
